import { RadioFilterGroup, RadioFilter, SelectFilter } from '@okam/brand-ui'
import { useIsClient } from '@okam/shared-ui'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import type { Key } from 'react'
import React, { useState, useEffect, useRef } from 'react'
import { Item } from 'react-stately'
import tw, { theme } from 'twin.macro'
import { useMediaQuery } from 'usehooks-ts'
import { trackImportantClick } from 'gtm-analytics/tracking'
import type { TAnchorsBlock } from '../types'

const AnchorsBlock = ({ anchors, scrollToRef, isDirectusAnchors = true }: TAnchorsBlock) => {
  const [selected, setSelected] = useState<number | undefined>(undefined)

  const { isClient } = useIsClient()
  const isDesktop = useMediaQuery('(min-width: 768px') && isClient

  const { t } = useTranslation('common')

  const isInitialRender = useRef(false)

  const { asPath } = useRouter()
  const hashID = asPath.split('#')[1]

  useEffect(() => {
    if (isInitialRender.current) {
      const timer = setTimeout(() => {
        if (scrollToRef) scrollToRef(selected ?? 0)
      }, theme('animation.time.instant'))
      return () => clearTimeout(timer)
    }
    if (hashID) {
      document.querySelector(`#${hashID}`)?.scrollIntoView(true)
    }
    isInitialRender.current = true
    return undefined
  }, [selected]) /* eslint-disable-line */

  const anchorsAriaLabel = t('ARIA_LABEL.ANCHORS')

  const setSelectedAnchor = (value: Key) => {
    if (typeof value === 'string') {
      const atIndex = value.indexOf('@')
      const percentageIndex = value.indexOf('%')
      const anchorIndex = value.slice(atIndex + 1, percentageIndex)
      const label = value.slice(0, atIndex)
      const frLabel = value.slice(percentageIndex + 1)
      trackImportantClick({
        click_type: 'ancrage',
        interaction_location: 'page',
        link_url: null,
        link_text: label,
        link_text_fr: frLabel,
      })
      if (isDirectusAnchors) {
        setSelected(parseInt(anchorIndex, 10) - 1)
      } else {
        setSelected(parseInt(anchorIndex, 10))
      }
    } else {
      setSelected(value)
    }
  }

  return (
    <div tw="flex flex-col md:flex-row justify-center items-center gap-4 mb-4 sticky top-4 md:top-2 z-50">
      {isDesktop ? (
        <RadioFilterGroup
          aria-label={anchorsAriaLabel}
          bgColor={theme('colors.primary[3]')}
          hoverColor={theme('colors.primaryHover[3]')}
          variant="withBg"
          onChange={(value) => {
            setSelectedAnchor(value)
          }}
        >
          {anchors?.map((anchor) => {
            const anchorAriaLabel = t('ARIA_LABEL.ANCHOR', { anchor })
            return (
              <RadioFilter
                /* eslint-disable-next-line react/no-array-index-key */
                key={`${anchor.label}@${anchor.refIndex}%${anchor.frLabel}`}
                bgColor={theme('colors.primary[3]')}
                hoverColor={theme('colors.primaryHover[3]')}
                id={anchor.label ?? null}
                label={anchor.label}
                name={anchor.label}
                value={`${anchor.label}@${anchor.refIndex}%${anchor.frLabel}`}
                variant="withBg"
                aria-label={anchorAriaLabel}
              />
            )
          })}
        </RadioFilterGroup>
      ) : (
        <SelectFilter
          shouldPopoverFlip
          allowOutsideScrolling
          id={anchorsAriaLabel}
          name={anchorsAriaLabel}
          placeholderLabel={anchors?.[0]?.label ?? anchorsAriaLabel}
          bgColor={theme('colors.primary[3]')}
          popoverStyles={[tw`min-w-[19rem]`]}
          hoverColor={theme('colors.primaryHover[3]')}
          variant="default"
          onSelectionChange={(value) => {
            setSelectedAnchor(value)
          }}
        >
          {anchors.map((anchor) => {
            const anchorAriaLabel = t('ARIA_LABEL.ANCHOR', { anchor })
            return (
              /* eslint-disable-next-line react/no-array-index-key */
              <Item aria-label={anchorAriaLabel} key={`${anchor.label}@${anchor.refIndex}%${anchor.frLabel}`}>
                {anchor.label}
              </Item>
            )
          })}
        </SelectFilter>
      )}
    </div>
  )
}

export default AnchorsBlock
